import { Select } from '@base-ui/react/select'
import './dropdown-field.css'
import classNames from 'classnames'
import { ChevronDownIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useMemo } from 'react'
import type { ReadonlyDeep } from 'type-fest'
import type { DropdownChoiceId } from '@companion-app/shared/Model/Common.js'
import { useDropdownChoicesForSelect, type DropdownChoicesOrGroups } from './DropdownChoices.js'
import { MenuPortalContext } from './MenuPortalContext.js'

interface SimpleDropdownInputFieldProps {
	id: string | undefined
	className?: string
	choices: DropdownChoicesOrGroups | ReadonlyDeep<DropdownChoicesOrGroups>
	tooltip?: string
	value: DropdownChoiceId
	setValue: (value: DropdownChoiceId) => void
	disabled?: boolean
	checkValid?: (value: DropdownChoiceId) => boolean | undefined
	noOptionsMessage?: string
	badOptionPrefix?: string
	onBlur?: React.FocusEventHandler<HTMLButtonElement>
}

/**
 * A simple and lightweight dropdown input field.
 * Does not support searching, multi-select, custom values or other features.
 */
export const SimpleDropdownInputField = observer(function SimpleDropdownInputField({
	id,
	className,
	choices,
	tooltip,
	value,
	setValue,
	disabled,
	checkValid,
	noOptionsMessage,
	badOptionPrefix,
	onBlur,
}: SimpleDropdownInputFieldProps): React.JSX.Element {
	const menuPortal = useContext(MenuPortalContext)
	const { options, flatOptions } = useDropdownChoicesForSelect(choices as DropdownChoicesOrGroups)

	const flatItems = useMemo(() => flatOptions.map((o) => ({ value: o.value, label: o.label })), [flatOptions])

	const onChange = useCallback(
		(v: DropdownChoiceId | null) => {
			// Base UI can emit null (e.g. deselect, or value={null} after choices become empty) — ignore it
			if (v !== null) setValue(v)
		},
		[setValue]
	)

	const isKnownValue = flatItems.some((o) => o.value === value) || options.length === 0
	const itemsForLookup = useMemo(
		() =>
			isKnownValue
				? flatItems
				: [
						...flatItems,
						{
							value,
							label: badOptionPrefix ? `${badOptionPrefix}: ${value}` : `?? (${value})`,
						},
					],
		[isKnownValue, flatItems, value, badOptionPrefix]
	)

	const noOptionsMessageFull = noOptionsMessage || 'No options available'

	return (
		<div
			className={classNames(
				'dropdown-field',
				{
					'dropdown-field-invalid': !isKnownValue || checkValid?.(value) === false,
				},
				className
			)}
			title={tooltip}
		>
			<Select.Root<DropdownChoiceId>
				id={id}
				value={options.length === 0 ? null : value}
				onValueChange={onChange}
				items={itemsForLookup}
				disabled={disabled}
			>
				<Select.Trigger className="form-input dropdown-field-select-trigger" onBlur={onBlur}>
					<Select.Value
						className="dropdown-field-select-value"
						placeholder={options.length === 0 ? noOptionsMessageFull : 'Select...'}
					/>
					<Select.Icon className="dropdown-field-trigger">
						<ChevronDownIcon className="dropdown-field-icon" />
					</Select.Icon>
				</Select.Trigger>
				<Select.Portal container={menuPortal ?? undefined}>
					<Select.Positioner className="dropdown-field-positioner" alignItemWithTrigger={false}>
						<Select.Popup className="dropdown-field-popup">
							<Select.List className="dropdown-field-list">
								{!options.length && (
									<Select.Item key="no-options" value="" className="dropdown-field-item" disabled>
										<Select.ItemText>{noOptionsMessageFull}</Select.ItemText>
									</Select.Item>
								)}
								{!isKnownValue && (
									<Select.Item key={`bad-option-${String(value)}`} value={value} className="dropdown-field-item">
										<Select.ItemText>
											{badOptionPrefix ? `${badOptionPrefix}: ${value}` : `?? (${value})`}
										</Select.ItemText>
									</Select.Item>
								)}
								{options.map((item) => {
									if ('options' in item) {
										return (
											<Select.Group key={item.label} className="dropdown-field-group">
												<Select.GroupLabel className="dropdown-field-group-label">{item.label}</Select.GroupLabel>
												{item.options.map((opt) => (
													<Select.Item
														key={JSON.stringify(opt.value)}
														value={opt.value}
														className="dropdown-field-item"
													>
														<Select.ItemText>{opt.label}</Select.ItemText>
													</Select.Item>
												))}
											</Select.Group>
										)
									}
									return (
										<Select.Item key={JSON.stringify(item.value)} value={item.value} className="dropdown-field-item">
											<Select.ItemText>{item.label}</Select.ItemText>
										</Select.Item>
									)
								})}
							</Select.List>
						</Select.Popup>
					</Select.Positioner>
				</Select.Portal>
			</Select.Root>
		</div>
	)
})
